/**
 * P6 — agency portal domains, embedded as a section inside Portal settings.
 *
 * (Formerly its own page/route `AgencyDomainsPage`; the separate Domains tab was
 * removed and this UI now lives under Portal settings.) Two cards:
 *   1. "Your portal address" — the auto-provisioned {slug}.r.adup.io subdomain
 *      with copy + open + status chip. The backend provisions this on first GET.
 *   2. "Custom domain" — add a hostname → the backend returns a TXT record to
 *      add at the client's DNS provider → "Verify now" re-checks it. A status
 *      badge reflects pending_dns / verifying / active / failed and the page
 *      polls GET agency-domains every ~15s while any custom domain is settling.
 *
 * Rendered only when the caller has `portal.domains.manage` (owner/manager); the
 * host page (PortalBrandSettings) gates it, so this component assumes access.
 * Toasts via setMessage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  listAgencyDomains,
  addAgencyDomain,
  verifyAgencyDomain,
  deleteAgencyDomain,
  setPrimaryAgencyDomain,
  type AgencyDomain,
} from '../../../lib/api/client-reports/agency-domains-api';
import { refreshPortalLink } from '../../../lib/api/client-reports/client-reports-api';
import type { RootState } from '../../../store/store';
import {
  isValidFqdn,
  normalizeHostname,
  shouldPollDomains,
  findPrimarySubdomain,
  statusTone,
  STATUS_LABEL,
} from '../domain-helpers';
import '../client-reports.scss';

/** How often to re-poll while a custom domain is pending/verifying. */
const POLL_INTERVAL_MS = 15000;

/**
 * The "Make primary" action on a verified custom domain. Backend confirmed in
 * P6: `POST /api/v1/agency-domains/{id}/set-primary` (active-only). Enabled.
 */
const ENABLE_SET_PRIMARY = true;

function StatusChip({ domain }: { domain: AgencyDomain }) {
  const tone = statusTone(domain.status);
  return (
    <span
      className={`portal-domains__chip portal-domains__chip--${tone}`}
      title={domain.tls_status ? `TLS: ${domain.tls_status}` : undefined}
    >
      {STATUS_LABEL[domain.status] || domain.status}
    </span>
  );
}

const PortalDomainsSection = () => {
  const dispatch = useDispatch();

  const [domains, setDomains] = useState<AgencyDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [hostname, setHostname] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgencyDomain | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toast = useCallback(
    (message: string, messageType: 'success' | 'error' | 'warning') =>
      dispatch(setMessage({ message, messageType })),
    [dispatch]
  );

  // ---- "Refresh link": mint a fresh magic link for the ACTIVE brand's
  //      client and copy it (the portal expires client-side, so a new link is
  //      what re-authenticates — a DB reset can't). ----
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const activeShopName = useSelector((s: RootState) => {
    const slug = s.user?.profile?.active_shop_slug;
    const shops = (s.user?.profile?.all_shops || []) as Array<{
      slug?: string;
      name?: string;
    }>;
    return shops.find((sh) => sh?.slug === slug)?.name || slug || 'this brand';
  });
  const [refreshingLink, setRefreshingLink] = useState(false);

  const handleRefreshLink = useCallback(async () => {
    if (!activeShopSlug || refreshingLink) return;
    setRefreshingLink(true);
    try {
      const res = await refreshPortalLink(activeShopSlug);
      const url = res?.data?.magic_link_url;
      if (!url) {
        toast('Could not generate a portal link.', 'error');
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toast(
          `Fresh portal link for ${res.data.client_email} copied — paste it to your client.`,
          'success'
        );
      } catch {
        // Clipboard blocked — surface the link so it isn't lost.
        toast(`Fresh link generated: ${url}`, 'success');
      }
    } catch (err: any) {
      if (err?.code === 'no_active_client') {
        toast(
          `No portal client for ${activeShopName} yet — invite one from Team → Clients first.`,
          'warning'
        );
      } else {
        toast(err?.message || 'Could not refresh the portal link.', 'error');
      }
    } finally {
      setRefreshingLink(false);
    }
  }, [activeShopSlug, activeShopName, refreshingLink, toast]);

  // ---- "Open": open the portal SIGNED IN. The bare hostname has no session
  //      (the portal expires client-side), so plain navigation dead-ends on
  //      /expired. Mint a fresh magic link and open that instead; fall back to
  //      the bare hostname when minting isn't possible (e.g. no client yet).
  //      The tab is opened synchronously so popup blockers allow it. ----
  const handleOpenPortal = useCallback(
    async (e: React.MouseEvent, hostname: string) => {
      e.preventDefault();
      const win = window.open('', '_blank');
      if (win) win.opener = null;
      const fallback = `https://${hostname}`;
      const go = (url: string) => {
        if (win) win.location.replace(url);
        else window.open(url, '_blank', 'noopener');
      };
      if (!activeShopSlug) {
        go(fallback);
        return;
      }
      try {
        const res = await refreshPortalLink(activeShopSlug);
        const minted = res?.data?.magic_link_url;
        if (!minted) {
          go(fallback);
          return;
        }
        // The mint builds the URL on the agency's primary domain; keep the
        // token but honour the hostname the user actually clicked.
        try {
          const u = new URL(minted);
          u.host = hostname;
          go(u.toString());
        } catch {
          go(minted);
        }
      } catch {
        // No active client (or mint failed) — plain open, old behaviour.
        go(fallback);
      }
    },
    [activeShopSlug]
  );

  // ---- data load (silent variant used by polling) ----
  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const rows = await listAgencyDomains();
        setDomains(rows);
        setLoadError(null);
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? 'You do not have permission to manage portal domains.'
            : err?.message || 'Failed to load portal domains.';
        if (!silent) setLoadError(msg);
        else toast(msg, 'error');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // ---- polling: run while any custom domain is still settling ----
  const isPolling = useMemo(() => shouldPollDomains(domains), [domains]);

  useEffect(() => {
    if (!isPolling) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(() => {
      load(true);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isPolling, load]);

  // Stop everything on unmount.
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    []
  );

  const subdomain = useMemo(() => findPrimarySubdomain(domains), [domains]);
  const customDomains = useMemo(
    () => domains.filter((d) => d.type === 'custom'),
    [domains]
  );

  const normalized = normalizeHostname(hostname);
  const hostnameValid = isValidFqdn(normalized);
  const alreadyAdded = domains.some((d) => d.hostname === normalized);

  // ---- copy-to-clipboard ----
  const copy = useCallback(
    async (value: string, key: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopiedKey(key);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopiedKey(null), 1600);
      } catch {
        toast('Could not copy to the clipboard.', 'error');
      }
    },
    [toast]
  );

  // ---- actions ----
  const handleAdd = async () => {
    if (!hostnameValid || adding || alreadyAdded) return;
    setAdding(true);
    try {
      await addAgencyDomain(normalized);
      setHostname('');
      toast(
        'Custom domain added. Add the DNS record below, then verify.',
        'success'
      );
      await load(false);
    } catch (err: any) {
      toast(err?.message || 'Failed to add the custom domain.', 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (id: string) => {
    if (verifyingId) return;
    setVerifyingId(id);
    try {
      const updated = await verifyAgencyDomain(id);
      setDomains((prev) => prev.map((d) => (d.id === id ? updated : d)));
      if (updated.status === 'active') {
        toast('Domain verified — it is now active.', 'success');
      } else if (updated.status === 'failed') {
        toast(
          'Verification failed. Double-check the DNS record and try again.',
          'warning'
        );
      } else {
        toast('Still checking — DNS can take a while to propagate.', 'warning');
      }
    } catch (err: any) {
      toast(err?.message || 'Failed to verify the domain.', 'error');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleMakePrimary = async (id: string) => {
    if (primaryId) return;
    setPrimaryId(id);
    try {
      await setPrimaryAgencyDomain(id);
      toast('Primary domain updated.', 'success');
      await load(false);
    } catch (err: any) {
      toast(err?.message || 'Failed to set the primary domain.', 'error');
    } finally {
      setPrimaryId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteAgencyDomain(pendingDelete.id);
      toast('Custom domain removed.', 'success');
      setPendingDelete(null);
      await load(false);
    } catch (err: any) {
      toast(err?.message || 'Failed to remove the domain.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const renderCopyBtn = (value: string, key: string, label = 'Copy') => (
    <button
      type='button'
      className='portal-domains__copy-btn'
      onClick={() => copy(value, key)}
    >
      {copiedKey === key ? 'Copied ✓' : label}
    </button>
  );

  return (
    <>
      <div className='portal-settings__section-head'>
        <h3 className='portal-settings__section-title'>Portal domains</h3>
        <p className='portal-settings__section-hint'>
          The addresses your client portal is served from — your ready-made
          subdomain plus any custom domains. These apply to the whole agency.
        </p>
      </div>

      <div className='portal-domains'>
        {loading ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg='Loading domains…' />
          </div>
        ) : loadError ? (
          <div className='client-reports__error'>
            {loadError}
            <div style={{ marginTop: '1.4rem' }}>
              <MainSquareButton
                type='button'
                value='Retry'
                buttonType='light'
                normal
                onClick={() => load(false)}
              />
            </div>
          </div>
        ) : (
          <>
            {/* ---- Your portal address (subdomain) ---- */}
            <section className='portal-domains__card'>
              <div className='portal-domains__card-head'>
                <div>
                  <h3 className='portal-domains__card-title'>
                    Your portal address
                  </h3>
                  <p className='portal-domains__card-hint'>
                    Every agency gets a ready-to-use portal subdomain. Share it
                    with clients, or set up a custom domain below.
                  </p>
                </div>
              </div>

              {subdomain ? (
                <div className='portal-domains__row'>
                  <div className='portal-domains__host'>
                    <span className='portal-domains__hostname'>
                      {subdomain.hostname}
                    </span>
                    <StatusChip domain={subdomain} />
                    <span className='portal-domains__primary-tag'>Primary</span>
                  </div>
                  <div className='portal-domains__row-actions'>
                    {renderCopyBtn(subdomain.hostname, 'subdomain')}
                    <button
                      type='button'
                      className='portal-domains__copy-btn'
                      onClick={handleRefreshLink}
                      disabled={refreshingLink || !activeShopSlug}
                      title={`Generate a fresh portal sign-in link for ${activeShopName}'s client and copy it to the clipboard (no email sent)`}
                    >
                      {refreshingLink ? 'Refreshing…' : 'Refresh link'}
                    </button>
                    <a
                      className='portal-domains__open-link'
                      href={`https://${subdomain.hostname}`}
                      target='_blank'
                      rel='noreferrer noopener'
                      onClick={(e) => handleOpenPortal(e, subdomain.hostname)}
                      title='Open the portal signed in (mints a fresh link)'
                    >
                      Open ↗
                    </a>
                  </div>
                </div>
              ) : (
                <div className='portal-domains__muted'>
                  Provisioning your portal subdomain… refresh in a moment.
                </div>
              )}
            </section>

            {/* ---- Custom domain ---- */}
            <section className='portal-domains__card'>
              <div className='portal-domains__card-head'>
                <div>
                  <h3 className='portal-domains__card-title'>Custom domain</h3>
                  <p className='portal-domains__card-hint'>
                    Serve the client portal from your own domain (e.g.
                    reports.youragency.com). Add it here, then create the DNS
                    record we generate.
                  </p>
                </div>
              </div>

              <div className='portal-domains__add'>
                <input
                  className='portal-domains__input'
                  placeholder='reports.youragency.com'
                  value={hostname}
                  spellCheck={false}
                  autoCapitalize='none'
                  onChange={(e) => setHostname(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd();
                  }}
                />
                <MainSquareButton
                  type='button'
                  value={adding ? 'Adding…' : 'Add domain'}
                  buttonType='success'
                  normal
                  loading={adding}
                  disabled={!hostnameValid || adding || alreadyAdded}
                  onClick={handleAdd}
                />
              </div>
              {hostname.trim() !== '' && !hostnameValid && (
                <p className='portal-domains__validation'>
                  Enter a valid domain like reports.youragency.com (no http://,
                  paths or spaces).
                </p>
              )}
              {hostnameValid && alreadyAdded && (
                <p className='portal-domains__validation'>
                  That domain is already on the list below.
                </p>
              )}

              {customDomains.length === 0 ? (
                <div className='portal-domains__empty'>
                  No custom domains yet. Add one above to get started.
                </div>
              ) : (
                <div className='portal-domains__list'>
                  {customDomains.map((d) => {
                    const rec = d.verification_record;
                    const showRecord =
                      !!rec &&
                      (d.status === 'pending_dns' || d.status === 'failed');
                    return (
                      <div key={d.id} className='portal-domains__custom'>
                        <div className='portal-domains__row'>
                          <div className='portal-domains__host'>
                            <span className='portal-domains__hostname'>
                              {d.hostname}
                            </span>
                            <StatusChip domain={d} />
                            {d.is_primary && (
                              <span className='portal-domains__primary-tag'>
                                Primary
                              </span>
                            )}
                          </div>
                          <div className='portal-domains__row-actions'>
                            {d.status === 'active' && (
                              <a
                                className='portal-domains__open-link'
                                href={`https://${d.hostname}`}
                                target='_blank'
                                rel='noreferrer noopener'
                                onClick={(e) => handleOpenPortal(e, d.hostname)}
                                title='Open the portal signed in (mints a fresh link)'
                              >
                                Open ↗
                              </a>
                            )}
                            {d.status !== 'active' && (
                              <MainSquareButton
                                type='button'
                                value={
                                  verifyingId === d.id ? 'Checking…' : 'Verify now'
                                }
                                buttonType='light'
                                normal
                                loading={verifyingId === d.id}
                                disabled={verifyingId === d.id}
                                onClick={() => handleVerify(d.id)}
                              />
                            )}
                            {ENABLE_SET_PRIMARY &&
                              d.status === 'active' &&
                              !d.is_primary && (
                                <MainSquareButton
                                  type='button'
                                  value={
                                    primaryId === d.id
                                      ? 'Setting…'
                                      : 'Make primary'
                                  }
                                  buttonType='light'
                                  normal
                                  loading={primaryId === d.id}
                                  disabled={!!primaryId}
                                  onClick={() => handleMakePrimary(d.id)}
                                />
                              )}
                            <button
                              type='button'
                              className='portal-domains__delete-btn'
                              title={
                                d.is_primary
                                  ? 'The primary domain cannot be removed.'
                                  : 'Remove domain'
                              }
                              disabled={d.is_primary}
                              onClick={() => setPendingDelete(d)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {showRecord && rec && (
                          <div className='portal-domains__dns'>
                            <p className='portal-domains__dns-intro'>
                              Add this record at your DNS provider, then click{' '}
                              <strong>Verify now</strong>. DNS changes can take a
                              few minutes to propagate.
                            </p>
                            <div className='portal-domains__dns-grid'>
                              <div className='portal-domains__dns-field'>
                                <span className='portal-domains__dns-label'>
                                  Type
                                </span>
                                <code className='portal-domains__dns-value'>
                                  {rec.type}
                                </code>
                              </div>
                              <div className='portal-domains__dns-field'>
                                <span className='portal-domains__dns-label'>
                                  Name / Host
                                </span>
                                <div className='portal-domains__dns-copyrow'>
                                  <code className='portal-domains__dns-value'>
                                    {rec.name}
                                  </code>
                                  {renderCopyBtn(rec.name, `${d.id}-name`)}
                                </div>
                              </div>
                              <div className='portal-domains__dns-field'>
                                <span className='portal-domains__dns-label'>
                                  Value
                                </span>
                                <div className='portal-domains__dns-copyrow'>
                                  <code className='portal-domains__dns-value portal-domains__dns-value--wrap'>
                                    {rec.value}
                                  </code>
                                  {renderCopyBtn(rec.value, `${d.id}-value`)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {d.status === 'verifying' && (
                          <p className='portal-domains__dns-intro'>
                            Checking DNS and issuing a TLS certificate… this
                            updates automatically.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {isPolling && (
                <p className='portal-domains__polling'>
                  Auto-refreshing while domains finish verifying…
                </p>
              )}
            </section>
          </>
        )}
      </div>

      {/* ---- delete confirm ---- */}
      <Popup trigger={!!pendingDelete} setTrigger={() => setPendingDelete(null)}>
        <div className='report-workbench__confirm'>
          <h3 className='report-workbench__confirm-title'>Remove domain</h3>
          <p className='report-workbench__confirm-text'>
            Remove <strong>{pendingDelete?.hostname}</strong> from your portal?
            The client portal will stop serving from this domain. You can add it
            again later.
          </p>
          <div className='report-workbench__confirm-actions'>
            <MainSquareButton
              type='button'
              value='Cancel'
              buttonType='light'
              normal
              disabled={deleting}
              onClick={() => setPendingDelete(null)}
            />
            <MainSquareButton
              type='button'
              value={deleting ? 'Removing…' : 'Remove domain'}
              buttonType='danger'
              normal
              loading={deleting}
              disabled={deleting}
              onClick={confirmDelete}
            />
          </div>
        </div>
      </Popup>
    </>
  );
};

export default PortalDomainsSection;
